import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Batch, PaginatedBatchResponse } from "../../types/pages/batches";

interface BatchesState {
  batches: Batch[];
  loading: boolean;
  error: string | null;

  addBatchLoading: boolean;
  addBatchError: string | null;
  addBatchSuccess: boolean;

  editBatchLoading: boolean;
  editBatchError: string | null;
  editBatchSuccess: boolean;

  deleteBatchLoading: boolean;
  deleteBatchError: string | null;
  deleteBatchSuccess: boolean;

  generateProdSealLoading: boolean;
  generateProdSealError: string | null;
  generateProdSealSuccess: boolean;

  publishBatchLoading: boolean;
  publishBatchError: string | null;
  publishBatchSuccess: boolean;

  selectedBatch: Batch | null;

  fetchBatchLoading: boolean;
  fetchBatchError: string | null;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: number;
}

const initialState: BatchesState = {
  batches: [],
  loading: false,
  error: null,

  addBatchLoading: false,
  addBatchError: null,
  addBatchSuccess: false,

  editBatchLoading: false,
  editBatchError: null,
  editBatchSuccess: false,

  deleteBatchLoading: false,
  deleteBatchError: null,
  deleteBatchSuccess: false,

  generateProdSealLoading: false,
  generateProdSealError: null,
  generateProdSealSuccess: false,

  publishBatchLoading: false,
  publishBatchError: null,
  publishBatchSuccess: false,

  selectedBatch: null,

  fetchBatchLoading: false,
  fetchBatchError: null,
  totalCount: 0,
  page: 0,
  rowsPerPage: 10,
  sortBy: "created_at",
  sortOrder: -1,
};

const batchesSlice = createSlice({
  name: "batches",
  initialState,
  reducers: {
    fetchBatches: (
      state,
      action: PayloadAction<{
        page: number;
        rowsPerPage: number;
        sortBy?: string;
        sortOrder?: number;
        productId?: string;
      }>,
    ) => {},
    fetchBatchesStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchBatchesSuccess: (
      state,
      action: PayloadAction<PaginatedBatchResponse>,
    ) => {
      state.loading = false;
      state.batches = action.payload.data;
      state.totalCount = action.payload.total;
    },
    fetchBatchesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.rowsPerPage = action.payload;
      state.page = 0;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
      state.page = 0;
    },
    setSortOrder: (state, action: PayloadAction<number>) => {
      state.sortOrder = action.payload;
      state.page = 0;
    },

    addBatch: (
      state,
      action: PayloadAction<
        Omit<
          Batch,
          "_id" | "created_at" | "updated_at" | "organization" | "product"
        >
      >,
    ) => {},
    addBatchStart: (state) => {
      state.addBatchLoading = true;
      state.addBatchError = null;
      state.addBatchSuccess = false;
    },
    addBatchSuccess: (state, action: PayloadAction<Batch>) => {
      state.addBatchLoading = false;
      state.addBatchSuccess = true;
      state.batches.push(action.payload);
    },
    addBatchFailure: (state, action: PayloadAction<string>) => {
      state.addBatchLoading = false;
      state.addBatchError = action.payload;
      state.addBatchSuccess = false;
    },

    editBatch: (
      state,
      action: PayloadAction<{
        batchId: string;
        batchData: Partial<
          Omit<
            Batch,
            "id" | "created_at" | "updated_at" | "organization" | "product"
          >
        >;
      }>,
    ) => {},
    editBatchStart: (state) => {
      state.editBatchLoading = true;
      state.editBatchError = null;
      state.editBatchSuccess = false;
    },
    editBatchSuccess: (state, action: PayloadAction<Batch>) => {
      state.editBatchLoading = false;
      state.editBatchSuccess = true;
      const index = state.batches.findIndex(
        (b) => b._id === action.payload._id,
      );
      if (index !== -1) {
        state.batches[index] = action.payload;
      }
    },
    editBatchFailure: (state, action: PayloadAction<string>) => {
      state.editBatchLoading = false;
      state.editBatchError = action.payload;
      state.editBatchSuccess = false;
    },

    deleteBatch: (state, action: PayloadAction<string>) => {},
    deleteBatchStart: (state) => {
      state.deleteBatchLoading = true;
      state.deleteBatchError = null;
      state.deleteBatchSuccess = false;
    },
    deleteBatchSuccess: (state, action: PayloadAction<string>) => {
      state.deleteBatchLoading = false;
      state.deleteBatchSuccess = true;
      state.batches = state.batches.filter((b) => b._id !== action.payload);
    },
    deleteBatchFailure: (state, action: PayloadAction<string>) => {
      state.deleteBatchLoading = false;
      state.deleteBatchError = action.payload;
      state.deleteBatchSuccess = false;
    },

    generateProdSeal: (state, action: PayloadAction<string>) => {},
    generateProdSealStart: (state) => {
      state.generateProdSealLoading = true;
      state.generateProdSealError = null;
      state.generateProdSealSuccess = false;
    },
    generateProdSealSuccess: (state) => {
      state.generateProdSealLoading = false;
      state.generateProdSealSuccess = true;
    },
    generateProdSealFailure: (state, action: PayloadAction<string>) => {
      state.generateProdSealLoading = false;
      state.generateProdSealError = action.payload;
      state.generateProdSealSuccess = false;
    },

    publishBatch: (state, action: PayloadAction<string>) => {},
    publishBatchStart: (state) => {
      state.publishBatchLoading = true;
      state.publishBatchError = null;
      state.publishBatchSuccess = false;
    },
    publishBatchSuccess: (state, action: PayloadAction<Batch>) => {
      state.publishBatchLoading = false;
      state.publishBatchSuccess = true;
      // Update the batch in the list
      const index = state.batches.findIndex(
        (b) => b._id === action.payload._id,
      );
      if (index !== -1) {
        state.batches[index] = action.payload;
      }
      // Update selected batch if it's the same
      if (
        state.selectedBatch &&
        state.selectedBatch._id === action.payload._id
      ) {
        state.selectedBatch = action.payload;
      }
    },
    publishBatchFailure: (state, action: PayloadAction<string>) => {
      state.publishBatchLoading = false;
      state.publishBatchError = action.payload;
      state.publishBatchSuccess = false;
    },

    clearBatchesError: (state) => {
      state.error = null;
      state.addBatchError = null;
      state.addBatchSuccess = false;
      state.editBatchError = null;
      state.editBatchSuccess = false;
      state.deleteBatchError = null;
      state.deleteBatchSuccess = false;
      state.generateProdSealError = null;
      state.generateProdSealSuccess = false;
      state.publishBatchError = null;
      state.publishBatchSuccess = false;
    },

    fetchBatchById: (state, action: PayloadAction<string>) => {},
    fetchBatchStart: (state) => {
      state.fetchBatchLoading = true;
      state.fetchBatchError = null;
    },
    fetchBatchSuccess: (state, action: PayloadAction<Batch>) => {
      state.fetchBatchLoading = false;
      state.selectedBatch = action.payload;
    },
    fetchBatchFailure: (state, action: PayloadAction<string>) => {
      state.fetchBatchLoading = false;
      state.fetchBatchError = action.payload;
    },
    clearSelectedBatch: (state) => {
      state.selectedBatch = null;
      state.fetchBatchError = null;
    },
  },
});

export const {
  fetchBatches,
  fetchBatchesStart,
  fetchBatchesSuccess,
  fetchBatchesFailure,
  addBatch,
  addBatchStart,
  addBatchSuccess,
  addBatchFailure,
  editBatch,
  editBatchStart,
  editBatchSuccess,
  editBatchFailure,
  deleteBatch,
  deleteBatchStart,
  deleteBatchSuccess,
  deleteBatchFailure,
  clearBatchesError,
  generateProdSeal,
  generateProdSealStart,
  generateProdSealSuccess,
  generateProdSealFailure,
  publishBatch,
  publishBatchStart,
  publishBatchSuccess,
  publishBatchFailure,
  fetchBatchById,
  fetchBatchStart,
  fetchBatchSuccess,
  fetchBatchFailure,
  clearSelectedBatch,
  setPage,
  setRowsPerPage,
  setSortBy,
  setSortOrder,
} = batchesSlice.actions;

export default batchesSlice.reducer;
